import React, { ReactNode, useState, useMemo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

export interface TableColumnDef<T = any> {
  header: string;
  render: (row: T, index: number) => ReactNode;
  width?: string;
  align?: "left" | "center" | "right";
  /** Extract a raw value for sorting. Column is sortable when this is provided. */
  getValue?: (row: T) => any;
  /** Explicitly disable sorting even when getValue is provided. */
  sortable?: boolean;
}

interface DataTableProps<T = any> {
  rows: T[];
  columns: TableColumnDef<T>[];
  getRowId?: (row: T) => string;
  showRowNumber?: boolean;
  sx?: SxProps<Theme>;
  /** 1-based page number. Provide all 4 pagination props to enable pagination. */
  page?: number;
  count?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (perPage: number) => void;
  rowsPerPageOptions?: number[];
}

export function DataTable<T = any>({
  rows,
  columns,
  getRowId = (row: any) => row._id,
  showRowNumber = true,
  sx,
  page,
  count,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25],
}: DataTableProps<T>) {
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const hasPagination = page != null && count != null && rowsPerPage != null && onPageChange != null;
  const nrOffset = page != null && rowsPerPage != null ? (page - 1) * rowsPerPage : 0;
  const allColumns = useMemo<TableColumnDef<T>[]>(() => showRowNumber
    ? [{ header: "Nr", render: (_row: T, index: number) => nrOffset + index + 1, width: "60px" }, ...columns]
    : columns, [columns, showRowNumber, nrOffset]);

  const colSortable = (col: TableColumnDef<T>) => col.getValue != null && col.sortable !== false;

  const handleSort = (colIndex: number) => {
    if (sortCol === colIndex) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(colIndex);
      setSortDir("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (sortCol == null || !allColumns[sortCol]?.getValue) return rows;
    const getValue = allColumns[sortCol].getValue!;
    return [...rows].sort((a, b) => {
      const va = getValue(a);
      const vb = getValue(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "string" && typeof vb === "string") {
        return sortDir === "asc" ? va.localeCompare(vb, "de") : vb.localeCompare(va, "de");
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sortCol, sortDir, allColumns]);

  const tableHead = (
    <TableHead sx={{ bgcolor: "grey.50" }}>
      <TableRow>
        {allColumns.map((col, i) => (
          <TableCell
            key={i}
            align={col.align}
            sx={{ fontWeight: 600, width: col.width }}
            sortDirection={sortCol === i ? sortDir : false}
          >
            {colSortable(col) ? (
              <TableSortLabel
                active={sortCol === i}
                direction={sortCol === i ? sortDir : "asc"}
                onClick={() => handleSort(i)}
              >
                {col.header}
              </TableSortLabel>
            ) : (
              col.header
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const pagination = hasPagination ? (
    <TablePagination
      component="div"
      count={count}
      page={page - 1}
      onPageChange={(_e, newPage) => onPageChange(newPage + 1)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={onRowsPerPageChange ? (e) => {
        onRowsPerPageChange(parseInt(e.target.value, 10));
        onPageChange(1);
      } : undefined}
      rowsPerPageOptions={rowsPerPageOptions}
      labelRowsPerPage="Zeilen pro Seite"
      sx={{ "& .MuiTablePagination-toolbar": { flexWrap: "wrap", justifyContent: "center" } }}
    />
  ) : null;

  return (
    <>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table sx={[{ minWidth: 600 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
          {tableHead}
          <TableBody sx={{ textTransform: "capitalize" }}>
            {sortedRows?.length > 0 && sortedRows.map((row, index) => (
              <TableRow key={getRowId(row)} hover>
                {allColumns.map((col, i) => (
                  <TableCell key={i} align={col.align} sx={{ width: col.width, verticalAlign: "top" }}>
                    {col.render(row, index)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
    </>
  );
}
